import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import LoadingSpinner from "../common/LoadingSpinner";
import gradeService from "../../services/gradeService";
import revisorService from "../../services/revisorService";
import reportService from "../../services/reportService";
import {
  fmtDateTime,
  normalizeRole,
  PROFESSION_LABELS,
  PROFESSION_ORDER,
} from "./revisorUtils";

// A definitive-grades entry is identified by learner + class + semester. Two classes
// of the same semester (even within the same school) are fully independent entries.
function entryKey(learnerId, entry) {
  return `${learnerId}-${entry?.classId ?? "no-class"}-${entry?.semesterId}`;
}

function isSameEntry(a, b) {
  return (
    a?.learnerId === b?.learnerId &&
    a?.semesterId === b?.semesterId &&
    (a?.classId ?? null) === (b?.classId ?? null)
  );
}

// A review has exactly one positive outcome: CHECKED. FINALIZED is the legacy value
// from the retired two-step flow and is treated as identical.
function isCheckedStatus(reviewStatus) {
  return reviewStatus === "CHECKED" || reviewStatus === "FINALIZED";
}

function needsReviewerAttention(entry) {
  return entry?.reviewStatus === "PENDING" || entry?.resubmittedAfterRejection;
}

function sortSemesterEntries(entries) {
  return entries
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => {
      const attentionDiff =
        Number(needsReviewerAttention(b.entry)) -
        Number(needsReviewerAttention(a.entry));
      if (attentionDiff !== 0) return attentionDiff;
      return a.index - b.index;
    })
    .map(({ entry }) => entry);
}

function RevisorReviewsPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [expandedLearners, setExpandedLearners] = useState(new Set());
  const [expandedCheckedGroups, setExpandedCheckedGroups] = useState(
    new Set(),
  );
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);
  // Undoing a check is visible to the learner, so it is confirmed first.
  const [showUncheckModal, setShowUncheckModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [, setGradeRows] = useState([]);

  const isRevisor = useMemo(
    () => user?.roles?.some((role) => normalizeRole(role) === "REVISOR"),
    [user],
  );

  const professionFilter = searchParams.get("profession") || "ALL";
  const searchFilter = searchParams.get("search") || "";
  const semesterFilter = searchParams.get("semester") || "";
  const focusedLearnerId = searchParams.get("learnerId");

  const setFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === "ALL") next.delete(key);
    else next.set(key, value);
    setSearchParams(next);
  };

  const clearFilters = () => {
    setSearchParams(new URLSearchParams());
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await revisorService.getReviews();
      setGroups(data || []);

      const learnersWithOpenEntries = (data || []).flatMap((group) =>
        (group.learners || [])
          .filter((learner) =>
            (learner.semesters || []).some((entry) =>
              needsReviewerAttention(entry),
            ),
          )
          .map((learner) => learner.learnerId),
      );

      setExpandedLearners((prev) => {
        const next = new Set(prev);
        learnersWithOpenEntries.forEach((learnerId) => next.add(learnerId));
        if (focusedLearnerId) next.add(Number(focusedLearnerId));
        return next;
      });
    } catch (err) {
      console.error(err);
      setError("Could not load revisor reviews.");
    } finally {
      setLoading(false);
    }
  }, [focusedLearnerId]);

  useEffect(() => {
    if (isRevisor) load();
    else setLoading(false);
  }, [isRevisor, load]);

  const filteredGroups = useMemo(() => {
    let data = groups;

    if (professionFilter !== "ALL") {
      data = data.filter((group) => group.professionType === professionFilter);
    }

    return data
      .map((group) => {
        const learners = (group.learners || [])
          .map((learner) => {
            const semesters = (learner.semesters || []).filter((entry) => {
              const semesterOk =
                !semesterFilter ||
                (entry.semesterKey || "")
                  .toLowerCase()
                  .includes(semesterFilter.toLowerCase());
              return semesterOk;
            });

            const nameOk =
              !searchFilter ||
              learner.learnerName
                .toLowerCase()
                .includes(searchFilter.toLowerCase());

            if (!nameOk && semesters.length === 0) {
              return null;
            }

            return {
              ...learner,
              semesters: sortSemesterEntries(semesters),
            };
          })
          .filter(Boolean);

        return {
          ...group,
          learners,
        };
      })
      .filter((group) => group.learners.length > 0)
      .sort(
        (a, b) =>
          PROFESSION_ORDER.indexOf(a.professionType) -
          PROFESSION_ORDER.indexOf(b.professionType),
      );
  }, [groups, professionFilter, searchFilter, semesterFilter]);

  const allProfessions = useMemo(() => {
    const set = new Set(groups.map((group) => group.professionType));
    return [
      "ALL",
      ...PROFESSION_ORDER.filter((profession) => set.has(profession)),
    ];
  }, [groups]);

  const totalLearners = useMemo(
    () =>
      filteredGroups.reduce(
        (sum, group) => sum + (group.learners?.length || 0),
        0,
      ),
    [filteredGroups],
  );

  const totalEntries = useMemo(
    () =>
      filteredGroups.reduce(
        (sum, group) =>
          sum +
          (group.learners || []).reduce(
            (inner, learner) => inner + (learner.semesters?.length || 0),
            0,
          ),
        0,
      ),
    [filteredGroups],
  );

  const toggleLearner = (learnerId) => {
    setExpandedLearners((prev) => {
      const next = new Set(prev);
      if (next.has(learnerId)) next.delete(learnerId);
      else next.add(learnerId);
      return next;
    });
  };

  const toggleCheckedGroup = (learnerId) => {
    setExpandedCheckedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(learnerId)) next.delete(learnerId);
      else next.add(learnerId);
      return next;
    });
  };

  const selectPreferredReport = (data, semesterId) => {
    if (!data) return null;

    const semesterReports = data.semesterReports || [];
    const unassignedReports = data.unassignedReports || [];

    return (
      semesterReports.find((report) => report.semesterId === semesterId) ||
      semesterReports[0] ||
      unassignedReports.find((report) => report.semesterId === semesterId) ||
      unassignedReports[0] ||
      null
    );
  };

  const formatSemesterDisplay = (entry) => {
    return entry?.semesterLabel || entry?.semesterKey || "Semester";
  };

  /**
   * Chronological rank of an entry. semesterKey ("HS25", "FS26") is the only
   * field that compares across classes - a "4. Semester" of one class and of
   * another are not the same point in time. HS runs Aug-Jan and FS Feb-Jul,
   * so HS25 < FS26 < HS26.
   */
  const semesterRank = (entry) => {
    const match = `${entry?.semesterKey || entry?.semesterLabel || ""}`.match(
      /\b([FH])S\s?(\d{2})\b/i,
    );
    if (!match) return null;
    const year = 2000 + Number(match[2]);
    return match[1].toUpperCase() === "H" ? year * 2 : (year - 1) * 2 + 1;
  };

  /** Newest semester first; entries without a key sink to the bottom. */
  const byNewestSemester = (a, b) => {
    const rankA = semesterRank(a);
    const rankB = semesterRank(b);

    if (rankA !== rankB) {
      if (rankA == null) return 1;
      if (rankB == null) return -1;
      return rankB - rankA;
    }

    // Same semester: keep the classes of that semester next to each other.
    return (a?.className || "").localeCompare(b?.className || "");
  };

  const getReviewStatusBadge = (entry) => {
    if (entry.resubmittedAfterRejection) {
      return "bg-amber-100 text-amber-800 border border-amber-300";
    }
    if (isCheckedStatus(entry.reviewStatus)) {
      return "bg-green-100 text-green-700 border border-green-200";
    }
    if (entry.reviewStatus === "REJECTED") {
      return "bg-red-100 text-red-700 border border-red-200";
    }
    return "bg-gray-100 text-gray-600 border border-gray-200";
  };

  const getReviewStatusLabel = (entry) => {
    if (entry.resubmittedAfterRejection) return "Resubmitted";
    if (isCheckedStatus(entry.reviewStatus)) return "Checked";
    if (entry.reviewStatus === "REJECTED") return "Rejected";
    return "Pending";
  };

  const openDetail = async (learnerId, semesterId, classId) => {
    try {
      setDetailLoading(true);
      const data = await revisorService.getDetail(learnerId, semesterId, classId);
      setDetail(data);

      const rows = await gradeService.getSemesterDefinitiveGrades(
        semesterId,
        learnerId,
        data?.entry?.classId ?? data?.classId ?? classId,
      );
      setGradeRows(rows || []);

      if (previewUrl) {
        window.URL.revokeObjectURL(previewUrl);
        setPreviewUrl("");
      }

      const preferredReport = selectPreferredReport(data, semesterId);
      if (preferredReport) {
        const blobUrl = await reportService.getPreviewUrl(
          preferredReport.id,
          learnerId,
          false,
        );
        setPreviewUrl(blobUrl);
      }
    } catch (err) {
      console.error(err);
      setError("Could not load detail view.");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleReject = async () => {
    if (!detail?.entry?.learnerId || !detail?.entry?.semesterId) return;
    if (!rejectReason.trim()) {
      setError("Please provide a reason for rejection.");
      return;
    }

    try {
      setRejecting(true);
      setError("");
      await revisorService.reject(
        detail.entry.learnerId,
        detail.entry.semesterId,
        rejectReason,
        detail.entry.classId,
      );
      setShowRejectModal(false);
      setRejectReason("");
      await load();
      await openDetail(
        detail.entry.learnerId,
        detail.entry.semesterId,
        detail.entry.classId,
      );
    } catch (err) {
      console.error(err);
      setError(err?.response?.data || "Could not reject entry.");
    } finally {
      setRejecting(false);
    }
  };

  const runCheck = async (learnerId, semesterId, classId) => {
    try {
      setActionLoading(true);
      await revisorService.markChecked(learnerId, semesterId, classId);
      await load();

      if (isSameEntry(detail?.entry, { learnerId, semesterId, classId })) {
        setDetail(null);
        setGradeRows([]);
        if (previewUrl) {
          window.URL.revokeObjectURL(previewUrl);
          setPreviewUrl("");
        }
      }
    } catch (err) {
      console.error(err);
      setError(err?.response?.data || "Review action failed.");
    } finally {
      setActionLoading(false);
    }
  };

  // Undoing a check puts the entry back into the pending queue and shows it as
  // awaiting a check to the learner again, so the detail view stays open on it.
  const runUncheck = async (learnerId, semesterId, classId) => {
    try {
      setActionLoading(true);
      setError("");
      await revisorService.uncheck(learnerId, semesterId, classId);
      setShowUncheckModal(false);
      await load();
      await openDetail(learnerId, semesterId, classId);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data || "Could not undo the check.");
    } finally {
      setActionLoading(false);
    }
  };

  const openDeleteModal = (learner, entry) => {
    setDeleteTarget({
      learnerId: learner.learnerId,
      learnerName: learner.learnerName,
      semesterId: entry.semesterId,
      classId: entry.classId ?? null,
      semesterLabel: formatSemesterDisplay(entry),
    });
    setDeleteConfirmText("");
  };

  const deleteMissingReportEntry = async () => {
    if (!deleteTarget) return;

    try {
      setActionLoading(true);
      setError("");
      await revisorService.deleteReviewEntry(
        deleteTarget.learnerId,
        deleteTarget.semesterId,
        deleteTarget.classId,
      );

      if (isSameEntry(detail?.entry, deleteTarget)) {
        setDetail(null);
        if (previewUrl) {
          window.URL.revokeObjectURL(previewUrl);
          setPreviewUrl("");
        }
      }

      setDeleteTarget(null);
      setDeleteConfirmText("");
      await load();
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.data?.message ||
          err?.response?.data ||
          "Could not delete revision entry.",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const resetAllRevisions = async () => {
    try {
      setActionLoading(true);
      setError("");
      const result = await revisorService.resetAllRevisions();
      setDetail(null);
      if (previewUrl) {
        window.URL.revokeObjectURL(previewUrl);
        setPreviewUrl("");
      }
      setResetConfirmText("");
      setShowResetModal(false);
      await load();
      if (result?.deletedEntries !== undefined) {
        setError(
          `Deleted ${result.deletedEntries} revision${
            result.deletedEntries === 1 ? "" : "s"
          }.`,
        );
      }
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.data?.message ||
          err?.response?.data ||
          "Could not delete revisions.",
      );
    } finally {
      setActionLoading(false);
    }
  };

  if (!isRevisor) {
    return (
      <div className="min-h-screen bg-ice-white flex items-center justify-center p-6">
        <div className="card-brand p-8 max-w-xl text-center">
          <h2 className="text-xl font-bold text-grey-dusk mb-2">
            Access denied
          </h2>
          <p className="text-slate-cloud">
            This area is only available for users with the Revisor role.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <LoadingSpinner message="Loading review control center..." />;
  }

  return (
    <div className="min-h-screen bg-green-cloud">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="card-brand-elevated p-6 md:p-8 bg-gradient-to-r from-ice-white to-pale-dawn/40">
            <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-5">
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-grey-dusk flex items-center mb-2">
                  <i className="fas fa-shield-alt text-sunrise-red mr-4"></i>
                  Data Check
                </h1>
                <p className="text-slate-cloud">
                  Review learner data, definitive grades and uploaded reports
                  before final confirmation.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 w-full xl:w-auto">
                <KpiCard
                  label="Professions"
                  value={filteredGroups.length}
                  icon="fas fa-layer-group"
                />
                <KpiCard
                  label="Apprentices"
                  value={totalLearners}
                  icon="fas fa-user-graduate"
                />
                <KpiCard
                  label="Entries"
                  value={totalEntries}
                  icon="fas fa-list-check"
                />
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 mb-6">
            {error}
          </div>
        )}

        {/* Filters */}
        <div className="card-brand p-4 sm:p-5 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
            <div>
              <h2 className="text-lg font-bold text-grey-dusk flex items-center">
                <i className="fas fa-filter mr-2 text-sunrise-red"></i>
                Filters
              </h2>
              <p className="text-sm text-slate-cloud mt-1">
                Narrow the list by profession or learner name.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            {allProfessions.map((profession) => (
              <button
                key={profession}
                onClick={() => setFilter("profession", profession)}
                className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                  professionFilter === profession
                    ? "bg-sunrise-red text-white border-sunrise-red"
                    : "bg-white text-grey-dusk border-pale-dawn hover:border-sunrise-red/30"
                }`}
              >
                {PROFESSION_LABELS[profession] || profession}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto] gap-3">
            <div className="relative">
              <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              <input
                className="input-brand pl-10"
                placeholder="Search learner name"
                value={searchFilter}
                onChange={(e) => setFilter("search", e.target.value)}
              />
            </div>

            <button
              onClick={clearFilters}
              className="px-4 py-2 rounded-lg bg-ice-white border border-pale-dawn text-grey-dusk font-medium hover:bg-white transition-colors"
            >
              Clear filters
            </button>
          </div>
        </div>

        {/* Groups */}
        <div className="space-y-5">
          {filteredGroups.map((group) => (
            <div
              key={group.professionType}
              className="card-brand overflow-hidden"
            >
              <div className="bg-gradient-to-r from-grey-dusk to-slate-cloud text-white px-5 py-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-bold">
                      {PROFESSION_LABELS[group.professionType] ||
                        group.professionType}
                    </h2>
                    <p className="text-sm text-white/80 mt-1">
                      {(group.learners || []).length} learner
                      {(group.learners || []).length === 1 ? "" : "s"}
                    </p>
                  </div>

                  <span className="self-start sm:self-auto px-3 py-1 rounded-full text-xs font-semibold bg-white/10 border border-white/20">
                    {group.professionType}
                  </span>
                </div>
              </div>

              <div className="p-4 sm:p-5 space-y-3">
                {group.learners.map((learner) => {
                  const expanded = expandedLearners.has(learner.learnerId);
                  const openEntryCount = (learner.semesters || []).filter(
                    needsReviewerAttention,
                  ).length;
                  const openEntries = (learner.semesters || []).filter(
                    needsReviewerAttention,
                  );
                  // Checked/returned entries read as a history, so the most
                  // recent semester belongs at the top.
                  const compactEntries = (learner.semesters || [])
                    .filter((entry) => !needsReviewerAttention(entry))
                    .sort(byNewestSemester);
                  const compactExpanded = expandedCheckedGroups.has(
                    learner.learnerId,
                  );

                  return (
                    <div
                      key={learner.learnerId}
                      className={`rounded-2xl border bg-white overflow-hidden ${
                        openEntryCount
                          ? "border-amber-300 shadow-sm shadow-amber-100"
                          : "border-pale-dawn"
                      }`}
                    >
                      <button
                        onClick={() => toggleLearner(learner.learnerId)}
                        className={`w-full px-4 py-4 flex items-center justify-between text-left transition-colors ${
                          openEntryCount
                            ? "bg-amber-50 hover:bg-amber-100/70"
                            : "hover:bg-ice-white/60"
                        }`}
                      >
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <div className="font-semibold text-grey-dusk truncate">
                              {learner.learnerName}
                            </div>
                            {openEntryCount > 0 && (
                              <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-200 text-amber-900 border border-amber-300">
                                {openEntryCount} to check
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-slate-cloud mt-1">
                            {learner.semesters.length} semester entries
                            {learner.learnerYear
                              ? ` · ${learner.learnerYear}. Lehrjahr`
                              : ""}
                          </div>
                        </div>

                        <div className="ml-4 flex items-center gap-3">
                          <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-ice-white border border-pale-dawn text-grey-dusk">
                            {learner.semesters.length}
                          </span>
                          <i
                            className={`fas fa-chevron-${expanded ? "up" : "down"} text-slate-cloud`}
                          />
                        </div>
                      </button>

                      {expanded && (
                        <div className="px-4 pb-4">
                          <div className="space-y-3">
                            {openEntries.map((entry) => (
                              <div
                                key={entryKey(learner.learnerId, entry)}
                                className="rounded-xl border border-amber-300 bg-amber-50 p-4 shadow-sm transition-colors"
                              >
                                  <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-4">
                                    <div className="min-w-0 flex-1">
                                      <div className="flex flex-wrap items-center gap-2 mb-2">
                                        <div className="font-semibold text-grey-dusk">
                                          {formatSemesterDisplay(entry)}
                                        </div>
                                        <span className="px-2 py-1 rounded-full text-[11px] font-bold bg-amber-200 text-amber-900 border border-amber-300">
                                          {entry.resubmittedAfterRejection
                                            ? "Needs re-check"
                                            : "Needs check"}
                                        </span>
                                        <span
                                          className={`px-2 py-1 rounded-full text-[11px] font-semibold ${getReviewStatusBadge(
                                            entry,
                                          )}`}
                                        >
                                          {getReviewStatusLabel(entry)}
                                        </span>
                                      </div>

                                      <div className="text-xs text-slate-cloud mb-2">
                                        {entry.schoolYear
                                          ? `School year ${entry.schoolYear}`
                                          : "PDF report required revision"}
                                      </div>

                                      {entry.uploadedReports?.length > 0 && (
                                        <div className="text-xs text-slate-cloud break-words">
                                          {entry.uploadedReports
                                            .map((r) => r.originalFilename)
                                            .join(" · ")}
                                        </div>
                                      )}
                                    </div>

                                    <div className="flex flex-wrap gap-2 text-[11px]">
                                      <span
                                        className={`px-2 py-1 rounded-full font-semibold ${
                                          entry.definitiveGradesAvailable
                                            ? "bg-blue-100 text-blue-700 border border-blue-200"
                                            : "bg-gray-100 text-gray-500 border border-gray-200"
                                        }`}
                                      >
                                        Grades{" "}
                                        {entry.definitiveGradesAvailable
                                          ? "ready"
                                          : "missing"}
                                      </span>
                                      <span
                                        className={`px-2 py-1 rounded-full font-semibold ${
                                          entry.reportsAvailable
                                            ? "bg-green-100 text-green-700 border border-green-200"
                                            : "bg-gray-100 text-gray-500 border border-gray-200"
                                        }`}
                                      >
                                        Reports{" "}
                                        {entry.reportsAvailable
                                          ? "ready"
                                          : "missing"}
                                      </span>
                                    </div>
                                  </div>

                                  <div className="mt-4 flex flex-wrap gap-2">
                                    {entry.resubmittedAfterRejection &&
                                      entry.rejectionReason && (
                                        <div className="mt-3 rounded-lg border border-amber-200 bg-white/80 px-3 py-2 text-xs text-amber-900">
                                          <span className="font-semibold">
                                            Previous rejection:
                                          </span>{" "}
                                          {entry.rejectionReason}
                                        </div>
                                      )}
                                  </div>

                                  <div className="mt-4 flex flex-wrap gap-2">
                                    <button
                                      onClick={() =>
                                        openDetail(
                                          learner.learnerId,
                                          entry.semesterId,
                                          entry.classId,
                                        )
                                      }
                                      className="px-3 py-2 rounded-lg bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 transition-colors"
                                    >
                                      <i className="fas fa-folder-open mr-2" />
                                      Open details to check
                                    </button>

                                    {!entry.reportsAvailable && (
                                      <button
                                        onClick={() =>
                                          openDeleteModal(learner, entry)
                                        }
                                        disabled={actionLoading}
                                        className="h-10 w-10 rounded-lg border border-slate-200 bg-white text-slate-400 hover:text-red-600 hover:border-red-200 disabled:opacity-50 transition-colors"
                                        title="Delete missing-report revision"
                                        aria-label="Delete missing-report revision"
                                      >
                                        <i className="fas fa-trash-alt text-sm" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                            ))}

                            {compactEntries.length > 0 && (
                              <div className="rounded-xl border border-pale-dawn bg-ice-white/50 overflow-hidden">
                                <button
                                  onClick={() =>
                                    toggleCheckedGroup(learner.learnerId)
                                  }
                                  className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-white/70 transition-colors"
                                  aria-expanded={compactExpanded}
                                >
                                  <div className="min-w-0">
                                    <div className="text-sm font-semibold text-grey-dusk">
                                      Checked / returned entries
                                    </div>
                                    <div className="text-xs text-slate-cloud mt-0.5">
                                      {compactEntries.length} compact semester
                                      {compactEntries.length === 1 ? "" : "s"}
                                    </div>
                                  </div>
                                  <i
                                    className={`fas fa-chevron-${
                                      compactExpanded ? "up" : "down"
                                    } text-slate-cloud text-sm`}
                                  />
                                </button>

                                {compactExpanded && (
                                  <div className="border-t border-pale-dawn divide-y divide-pale-dawn bg-white">
                                    {compactEntries.map((entry) => (
                                      <div
                                        key={entryKey(learner.learnerId, entry)}
                                        className="px-4 py-3 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3"
                                      >
                                        <div className="min-w-0">
                                          <div className="flex flex-wrap items-center gap-2">
                                            <span className="text-sm font-semibold text-grey-dusk">
                                              {formatSemesterDisplay(entry)}
                                            </span>
                                            <span
                                              className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${getReviewStatusBadge(
                                                entry,
                                              )}`}
                                            >
                                              {getReviewStatusLabel(entry)}
                                            </span>
                                            <span
                                              className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                                                entry.definitiveGradesAvailable
                                                  ? "bg-blue-100 text-blue-700 border border-blue-200"
                                                  : "bg-gray-100 text-gray-500 border border-gray-200"
                                              }`}
                                            >
                                              Grades{" "}
                                              {entry.definitiveGradesAvailable
                                                ? "ready"
                                                : "missing"}
                                            </span>
                                            <span
                                              className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                                                entry.reportsAvailable
                                                  ? "bg-green-100 text-green-700 border border-green-200"
                                                  : "bg-gray-100 text-gray-500 border border-gray-200"
                                              }`}
                                            >
                                              Reports{" "}
                                              {entry.reportsAvailable
                                                ? "ready"
                                                : "missing"}
                                            </span>
                                          </div>
                                          {entry.uploadedReports?.length > 0 && (
                                            <div className="text-xs text-slate-cloud truncate mt-1">
                                              {entry.uploadedReports
                                                .map((r) => r.originalFilename)
                                                .join(" · ")}
                                            </div>
                                          )}
                                        </div>

                                        <div className="flex flex-wrap gap-2">
                                          <button
                                            onClick={() =>
                                              openDetail(
                                                learner.learnerId,
                                                entry.semesterId,
                                                entry.classId,
                                              )
                                            }
                                            className="px-3 py-1.5 rounded-lg border border-pale-dawn bg-white text-xs font-semibold text-grey-dusk hover:bg-ice-white transition-colors"
                                          >
                                            Open details
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}

                            {learner.unassignedReports?.length > 0 && (
                              <div className="rounded-xl border border-dashed border-pale-dawn bg-white p-4">
                                <div className="text-sm font-semibold text-grey-dusk mb-2">
                                  Reports without semester match
                                </div>
                                <div className="text-xs text-slate-cloud break-words">
                                  {learner.unassignedReports
                                    .map((r) => r.originalFilename)
                                    .join(" · ")}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {!filteredGroups.length && (
            <div className="card-brand p-8 text-center text-slate-cloud">
              No results for current filters.
            </div>
          )}
        </div>

        {/* Detail modal */}
        {detail && (
          <div
            className="fixed inset-0 z-[90] bg-black/40 flex items-end md:items-center justify-center p-4"
            onClick={() => setDetail(null)}
          >
            <div
              className="bg-white w-full max-w-5xl max-h-[92vh] overflow-y-auto rounded-2xl shadow-brand-xl"
              onClick={(e) => e.stopPropagation()}
            >
              {detailLoading ? (
                <div className="p-6">
                  <LoadingSpinner message="Loading detail..." />
                </div>
              ) : (
                <>
                  <div className="bg-gradient-to-r from-ice-white to-pale-dawn/30 border-b border-pale-dawn px-6 py-5">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="text-2xl font-bold text-grey-dusk">
                          Apprentice / Semester detail
                        </h3>
                        <p className="text-slate-cloud mt-1">
                          Review the grades and the report preview, then mark this
                          entry as checked.
                        </p>
                      </div>

                      <button
                        onClick={() => setDetail(null)}
                        className="w-10 h-10 rounded-xl bg-white border border-pale-dawn text-slate-cloud hover:text-grey-dusk hover:bg-ice-white transition-colors"
                      >
                        <i className="fas fa-times" />
                      </button>
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-5">
                      <InfoCard title="Apprentice">
                        <div>{detail.entry.learnerName}</div>
                        <div>
                          {PROFESSION_LABELS[detail.entry.professionType] ||
                            detail.entry.professionType}
                        </div>
                        <div>{formatSemesterDisplay(detail.entry)}</div>
                      </InfoCard>

                      <div className="rounded-xl border border-pale-dawn p-4 bg-ice-white">
                        <h4 className="font-semibold text-grey-dusk mb-3">
                          Definitive grades
                        </h4>
                        <div className="space-y-2 text-sm">
                          {(detail.definitiveGrades || []).length ? (
                            (detail.definitiveGrades || []).map((grade) => (
                              <div
                                key={grade.subjectId}
                                className="flex justify-between items-center gap-3 rounded-lg bg-white border border-pale-dawn px-3 py-2"
                              >
                                <span className="text-grey-dusk min-w-0">
                                  {/* IT and MEDI modules are identified by
                                      their code; subjects without one just
                                      show the name. */}
                                  {grade.subjectCode && (
                                    <span className="mr-2 px-1.5 py-0.5 rounded bg-ice-white border border-pale-dawn text-[11px] font-semibold text-slate-cloud align-middle">
                                      {grade.subjectCode}
                                    </span>
                                  )}
                                  {grade.subject}
                                </span>
                                <span className="font-bold text-sunrise-red shrink-0">
                                  {grade.definitiveGrade}
                                </span>
                              </div>
                            ))
                          ) : (
                            <div className="text-slate-cloud">
                              No definitive grades available.
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    {/*
                    <div className="rounded-2xl border border-pale-dawn bg-ice-white p-4 mb-5">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
                        <div>
                          <h4 className="font-semibold text-grey-dusk">
                            Edit definitive grades
                          </h4>
                          <p className="text-xs text-slate-cloud mt-1">
                            Review the final semester grades here. Revisor editing is disabled.
                          </p>
                        </div>
                      </div>

                      {!gradeRows.length ? (
                        <div className="text-sm text-slate-cloud">
                          No definitive grades found for this semester.
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {gradeRows.map((row) => (
                            <div
                              key={row.subjectId}
                              className="rounded-xl border border-pale-dawn bg-white p-3 flex justify-between items-center"
                            >
                              <div className="text-sm font-semibold text-grey-dusk">
                                {row.subjectName}
                              </div>
                              <div className="text-sm font-bold bg-ice-white px-3 py-1 rounded">
                                {row.definitiveGrade || row.effectiveGrade || row.suggestedDefinitiveGrade || "-"}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    */}

                    {previewUrl && (
                      <div className="mt-4 rounded-xl border border-pale-dawn overflow-hidden bg-white">
                        <div className="px-4 py-3 border-b border-pale-dawn bg-ice-white text-sm font-semibold text-grey-dusk">
                          Report preview
                        </div>
                        <iframe
                          title="Report preview"
                          src={previewUrl}
                          className="w-full h-[360px]"
                        />
                      </div>
                    )}

                    <div className="mt-5 border-t border-pale-dawn pt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                      <div className="text-sm text-slate-cloud">
                        {isCheckedStatus(detail.entry.reviewStatus) ? (
                          <span className="text-green-600 font-semibold mb-1 block">
                            <i className="fas fa-check-double mr-1"></i>
                            Checked on {fmtDateTime(detail.entry.reviewedAt)} by{" "}
                            {detail.entry.reviewedBy}
                          </span>
                        ) : detail.entry.resubmittedAfterRejection ? (
                          <span className="text-amber-700 font-semibold mb-1 block">
                            <i className="fas fa-redo mr-1"></i>
                            Resubmitted after rejection. Previous reason: <br />"
                            {detail.entry.rejectionReason}"
                          </span>
                        ) : detail.entry.reviewStatus === "REJECTED" &&
                          detail.entry.rejectedAt ? (
                          <span className="text-sunrise-red font-semibold mb-1 block">
                            <i className="fas fa-undo mr-1"></i>
                            Rejected on {fmtDateTime(
                              detail.entry.rejectedAt,
                            )}{" "}
                            by {detail.entry.rejectedBy} for reason: <br /> "
                            {detail.entry.rejectionReason}"
                          </span>
                        ) : (
                          <span>Not checked yet.</span>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2 justify-end">
                        {isCheckedStatus(detail.entry.reviewStatus) && (
                          <button
                            onClick={() => {
                              setError("");
                              setShowUncheckModal(true);
                            }}
                            disabled={actionLoading}
                            className="px-4 py-2 rounded-lg bg-white border border-slate-cloud text-slate-cloud font-semibold hover:bg-pale-dawn disabled:opacity-50"
                            title="Set this entry back to awaiting check"
                          >
                            <i className="fas fa-rotate-left mr-2" />
                            Undo check
                          </button>
                        )}

                        {!isCheckedStatus(detail.entry.reviewStatus) && (
                          <button
                            onClick={() => setShowRejectModal(true)}
                            disabled={actionLoading}
                            className="px-4 py-2 rounded-lg bg-white border border-sunrise-red text-sunrise-red font-semibold hover:bg-sunrise-50 disabled:opacity-50"
                          >
                            Reject / Send back
                          </button>
                        )}

                        {detail.entry.canCheck && (
                          <button
                            onClick={() =>
                              runCheck(
                                detail.entry.learnerId,
                                detail.entry.semesterId,
                                detail.entry.classId,
                              )
                            }
                            disabled={actionLoading}
                            className="px-4 py-2 rounded-lg bg-amber-500 text-white font-semibold hover:bg-amber-600 disabled:opacity-50"
                          >
                            <i className="fas fa-check mr-2" />
                            {detail.entry.resubmittedAfterRejection
                              ? "Accept & mark as checked"
                              : "Mark as checked"}
                          </button>
                        )}

                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Reset modal */}
        {showResetModal && (
          <ConfirmDangerModal
            title="Reset all revisions"
            subtitle="This will set all review entries back to pending and clear the reviewer stamps."
            hint="Safety check: type RESET to enable this action."
            placeholder="Type RESET"
            confirmValue={resetConfirmText}
            setConfirmValue={setResetConfirmText}
            requiredText="RESET"
            cancelLabel="Cancel"
            confirmLabel={actionLoading ? "Deleting..." : "Delete now"}
            onCancel={() => {
              if (actionLoading) return;
              setShowResetModal(false);
              setResetConfirmText("");
            }}
            onConfirm={resetAllRevisions}
            loading={actionLoading}
          />
        )}

        {/* Delete modal */}
        {deleteTarget && (
          <ConfirmDangerModal
            title="Delete missing-report revision"
            subtitle={
              <>
                <div>
                  {deleteTarget.learnerName} - {deleteTarget.semesterLabel}
                </div>
                <div className="mt-1">
                  This entry has no uploaded PDF report and can be removed so
                  you can start over.
                </div>
              </>
            }
            hint="Safety check: type DELETE to enable this action."
            placeholder="Type DELETE"
            confirmValue={deleteConfirmText}
            setConfirmValue={setDeleteConfirmText}
            requiredText="DELETE"
            cancelLabel="Cancel"
            confirmLabel={actionLoading ? "Deleting..." : "Delete now"}
            onCancel={() => {
              if (actionLoading) return;
              setDeleteTarget(null);
              setDeleteConfirmText("");
            }}
            onConfirm={deleteMissingReportEntry}
            loading={actionLoading}
          />
        )}

        {/* Reject modal */}
        {showUncheckModal && detail?.entry && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-brand-xl border border-pale-dawn">
              <div className="flex items-center gap-3 mb-3">
                <span className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                  <i className="fas fa-rotate-left"></i>
                </span>
                <h3 className="text-xl font-bold text-grey-dusk">
                  Undo this check?
                </h3>
              </div>

              <p className="text-sm text-slate-cloud mb-3">
                <span className="font-semibold text-grey-dusk">
                  {detail.entry.learnerName}
                </span>{" "}
                · {formatSemesterDisplay(detail.entry)}
              </p>

              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 mb-4">
                <p className="text-xs font-semibold text-amber-800 mb-2">
                  What happens next:
                </p>
                <ul className="text-xs text-amber-800 space-y-1 list-disc list-inside">
                  <li>The entry returns to the open queue awaiting a check.</li>
                  <li>
                    The apprentice sees this semester as not yet checked again.
                  </li>
                  <li>
                    The record of who checked it and when is cleared and is not
                    restored if you check it again.
                  </li>
                </ul>
              </div>

              {error && (
                <div className="mb-4 bg-red-50 text-red-600 text-sm p-3 rounded-lg flex items-start">
                  <i className="fas fa-exclamation-circle mt-0.5 mr-2"></i>
                  {error}
                </div>
              )}

              <div className="flex justify-end gap-3">
                <button
                  className="px-6 py-3 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors text-sm font-medium"
                  onClick={() => {
                    setShowUncheckModal(false);
                    setError("");
                  }}
                  disabled={actionLoading}
                >
                  Keep it checked
                </button>
                <button
                  className="px-4 py-2 rounded-lg font-medium bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 inline-flex items-center gap-2"
                  onClick={() =>
                    runUncheck(
                      detail.entry.learnerId,
                      detail.entry.semesterId,
                      detail.entry.classId,
                    )
                  }
                  disabled={actionLoading}
                >
                  {actionLoading ? (
                    <>
                      <i className="fas fa-circle-notch fa-spin"></i>
                      Undoing...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-rotate-left"></i>
                      Yes, undo check
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {showRejectModal && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-brand-xl border border-pale-dawn">
              <h3 className="text-xl font-bold text-sunrise-red mb-2">
                Reject / Send Back
              </h3>
              <p className="text-sm text-slate-cloud mb-4">
                Please provide a reason to send this semester back for
                correction. This will unlock the definitive grades for editing
                and notify the learner.
              </p>

              {error && (
                <div className="mb-4 bg-red-50 text-red-600 text-sm p-3 rounded-lg flex items-start">
                  <i className="fas fa-exclamation-circle mt-0.5 mr-2"></i>
                  {error}
                </div>
              )}

              <textarea
                className="w-full input-brand min-h-[100px] mb-6"
                placeholder="E.g., 'One subject is missing' or 'Zeugnis note does not match semester grade...'"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                disabled={rejecting}
              />

              <div className="flex justify-end gap-3">
                <button
                  className="px-6 py-3 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors text-sm font-medium flex items-center justify-center"
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectReason("");
                    setError("");
                  }}
                  disabled={rejecting}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 rounded-lg font-medium bg-sunrise-red text-white hover:bg-sunrise-600 disabled:opacity-50"
                  onClick={handleReject}
                  disabled={rejecting || !rejectReason.trim()}
                >
                  {rejecting ? "Sending..." : "Reject entry"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function KpiCard({ label, value, icon }) {
  return (
    <div className="bg-white border border-pale-dawn rounded-xl p-4 min-w-[150px]">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-xs text-gray-500">{label}</div>
          <div className="text-xl font-bold text-grey-dusk mt-1">{value}</div>
        </div>
        <div className="w-10 h-10 rounded-xl bg-sunrise-red/10 text-sunrise-red flex items-center justify-center">
          <i className={icon}></i>
        </div>
      </div>
    </div>
  );
}

function InfoCard({ title, children }) {
  return (
    <div className="rounded-xl border border-pale-dawn bg-white p-4">
      <div className="text-sm font-semibold text-grey-dusk mb-3">{title}</div>
      <div className="text-sm text-slate-cloud space-y-1">{children}</div>
    </div>
  );
}

function ConfirmDangerModal({
  title,
  subtitle,
  hint,
  placeholder,
  confirmValue,
  setConfirmValue,
  requiredText,
  cancelLabel,
  confirmLabel,
  onCancel,
  onConfirm,
  loading,
}) {
  return (
    <div
      className="fixed inset-0 z-[96] bg-black/45 flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white border border-pale-dawn shadow-brand-xl p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-3">
          <div className="h-9 w-9 rounded-lg bg-red-50 text-red-600 flex items-center justify-center shrink-0">
            <i className="fas fa-trash-alt" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-grey-dusk">{title}</h3>
            <div className="text-sm text-slate-cloud mt-1">{subtitle}</div>
          </div>
        </div>

        <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-xs px-3 py-2 mb-3">
          {hint}
        </div>

        <input
          className="input-brand mb-4"
          placeholder={placeholder}
          value={confirmValue}
          onChange={(e) => setConfirmValue(e.target.value)}
        />

        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-3 py-1.5 rounded-lg border border-pale-dawn bg-white text-sm font-semibold text-slate-cloud hover:text-grey-dusk"
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            disabled={loading || confirmValue.trim() !== requiredText}
            className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default RevisorReviewsPage;
